import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import debounce from 'debounce-promise'

import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'

import { filterAirportRecords } from 'components/SearchV2/Utils/GlobalSearchV2'
import { KEYCODE_ENTER } from 'constants/keyCode'
import { getAirportsSearch } from 'api/flights'
import useDidChange from 'hooks/useDidChange'

export interface LocationSearchFieldProps {
  selectRef: React.MutableRefObject<any>;
  onSelect: (airport: App.AirportLocation) => void;
  isDeparture: boolean;
}

const useFlightLocationSearchFieldProps = (props: LocationSearchFieldProps) => {
  const {
    selectRef,
    onSelect,
    isDeparture,
  } = props

  const { departureAirport, arrivalAirport } = useContext(FlightSearchWidgetStateContext)
  const globalSearchDispatch = useContext(FlightSearchWidgetDispatchContext)

  const [isSettingValue, setIsSettingValue] = useState<boolean>(false)
  const airport = useMemo(() => isDeparture ? departureAirport : arrivalAirport, [isDeparture, departureAirport, arrivalAirport])
  const actionType = isDeparture ? FlightSearchWidgetActions.SET_DEPARTURE_AIRPORT : FlightSearchWidgetActions.SET_ARRIVAL_AIRPORT

  const [inputValue, setInputValue] = useState('')

  const hasChangedLocation = useDidChange(airport)

  // This logic is used to fire onSelect method only when state is updated
  useEffect(() => {
    if (isSettingValue && hasChangedLocation && airport) {
      setIsSettingValue(false)
      onSelect(airport)
    }
  }, [isSettingValue, hasChangedLocation, airport, onSelect])

  const setValue = useCallback((newAirport?: App.AirportLocation) => {
    if (newAirport === airport) return

    globalSearchDispatch({ type: actionType, airport: newAirport })

    if (newAirport) {
      setIsSettingValue(true)
    }
  }, [airport, globalSearchDispatch, actionType])

  const onChange = useCallback((airport: App.AirportLocation, { action }: { action: string }) => {
    if (action === 'clear') {
      setInputValue('')
      return
    } else if (action === 'select-option') {
      setInputValue('')
      setValue(airport)
    }
  }, [setInputValue, setValue])

  const onInputChange = useCallback((newInput: string, { action }: { action: string }) => {
    if (action === 'input-change') {
      setInputValue(newInput)
    }
  }, [setInputValue])

  const debouncedLoadAirports = useMemo(
    () => debounce((search: string, callback) => {
      getAirportsSearch(search).then(airports => {
        const filteredAirports = filterAirportRecords(airports, departureAirport, arrivalAirport)

        callback(filteredAirports)
      }).catch(() => callback(null))
    }, 500), [departureAirport, arrivalAirport])

  const handleOnKeyDown = (e) => {
    if (e.key === 'Enter' || e.keyCode === KEYCODE_ENTER) {
      if (selectRef.current.state.isLoading) {
        e.preventDefault()
      }
    }
  }

  const loadOptions = useCallback((search: string, callback) => {
    search.length > 1 ? debouncedLoadAirports(search, callback) : callback(null)
  }, [debouncedLoadAirports])

  const noOptionsMessage = useMemo(() => (null), [])

  return ({
    inputValue,
    setValue,
    handleOnKeyDown,
    loadOptions,
    onInputChange,
    noOptionsMessage,
    onChange,
  })
}

export default useFlightLocationSearchFieldProps
